/**
 * useTranscriptPersist — Persistencia incremental del transcript en vivo.
 *
 * Cada N segundos o N segmentos (lo que ocurra primero), escribe el lote
 * acumulado a `consultas/{id}/transcript_live/{batchId}` con merge=false.
 *
 * Esto permite:
 *   - Recovery: si el navegador se cierra, el transcript hasta ese momento
 *     queda persistido y disponible para revisión.
 *   - Auditoría en vivo: supervisor puede leer la conversación en tiempo
 *     casi-real desde Firestore.
 *   - Cierre robusto: la CF `portal_finalizarConsulta` puede consolidar
 *     todos los batches en `transcript/completo` al firmar (TODO).
 *
 * Diseño:
 *   - Buffer en memoria de segmentos nuevos no persistidos.
 *   - Flush cada `flushIntervalMs` (default 20s) o cada `flushBatchSize`
 *     segmentos finales (default 6).
 *   - Cada batch lleva su `batchSeq` (incremental) para reconstruir orden.
 */

import { useEffect, useRef } from 'react';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface TranscriptSegmentInput {
  id: number | string;
  speaker: string;
  text: string;
  ts?: number;
  isFinal?: boolean;
}

interface UseTranscriptPersistOptions {
  consultaId: string | undefined;
  segments: TranscriptSegmentInput[];
  flushIntervalMs?: number;   // default 20000
  flushBatchSize?: number;    // default 6
  enabled?: boolean;          // default true
}

export function useTranscriptPersist({
  consultaId,
  segments,
  flushIntervalMs = 20000,
  flushBatchSize = 6,
  enabled = true,
}: UseTranscriptPersistOptions) {
  const persistedIdsRef = useRef<Set<string>>(new Set());
  const seqRef = useRef(0);
  const pendingRef = useRef<TranscriptSegmentInput[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // Mantener un ref de segments para acceso síncrono en flush
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  // Ref a la última versión de flush para usar en listeners de eventos sin
  // recrearlos en cada render.
  const flushRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const flush = async () => {
    if (!consultaId || pendingRef.current.length === 0) return Promise.resolve();
    const batch = pendingRef.current;
    pendingRef.current = [];
    const batchSeq = ++seqRef.current;
    const batchId = `batch_${String(batchSeq).padStart(4, '0')}_${Date.now()}`;
    try {
      const ref = doc(collection(db, `consultas/${consultaId}/transcript_live`), batchId);
      await setDoc(ref, {
        batchSeq,
        guardadoEn: serverTimestamp(),
        segments: batch.map((s) => ({
          id: String(s.id),
          speaker: s.speaker,
          text: s.text.slice(0, 2000),
          ts: typeof s.ts === 'number' ? s.ts : Date.now(),
          isFinal: s.isFinal !== false,
        })),
      });
      // Marcar como persistidos
      for (const s of batch) persistedIdsRef.current.add(String(s.id));
    } catch (err) {
      // Restaurar al buffer para reintento
      pendingRef.current = [...batch, ...pendingRef.current];
      console.warn('[useTranscriptPersist] flush failed, restaurando buffer:', err);
    }
  };
  flushRef.current = flush;

  // Detectar nuevos segmentos finales y meterlos al pending
  useEffect(() => {
    if (!enabled || !consultaId) return;
    const nuevos = segments.filter(
      (s) => s.isFinal !== false && !persistedIdsRef.current.has(String(s.id))
        && !pendingRef.current.some((p) => String(p.id) === String(s.id))
    );
    if (nuevos.length === 0) return;
    pendingRef.current.push(...nuevos);
    if (pendingRef.current.length >= flushBatchSize) {
      flush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, enabled, consultaId, flushBatchSize]);

  // Timer de flush periódico
  useEffect(() => {
    if (!enabled || !consultaId) return;
    timerRef.current = setInterval(() => {
      flush();
    }, flushIntervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      // Flush final al desmontar
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, consultaId, flushIntervalMs]);

  // Flush best-effort ante cierre/ocultamiento de la pestaña o caída abrupta.
  // `pagehide` y `visibilitychange→hidden` se disparan cuando el navegador se
  // va a segundo plano o se cierra, casos donde el cleanup de desmontaje puede
  // no ejecutarse. Garantiza que la transcripción acumulada quede persistida.
  useEffect(() => {
    if (!enabled || !consultaId) return;
    const onHide = () => { flushRef.current(); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushRef.current(); };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, consultaId]);

  // Permite a quien usa el hook forzar un flush inmediato (p. ej. al detectar
  // desconexión de LiveKit) sin esperar al timer.
  return { flushNow: () => flushRef.current() };
}
